import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submitButton", "itemizedBills", "billsPopup", "arrow", "billsContainer"]
  static values = {
    upscalingCredit: Number // e.g. 0.5
  }

  connect() {
    // We store the selected files
    this.files = []
    // This map will track cost per file (though upscaling is a fixed cost, we'll keep the structure for clarity)
    this.costs = new Map()

    // Hide bills on load
    this.billsContainerTarget.classList.add("hidden")
    this.billsPopupTarget.classList.add("hidden")

    // For closing the popup when user clicks outside
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.billsPopupTarget.classList.add("hidden")
      this.arrowTarget.classList.remove("rotate-180")
    }
  }

  // Called when files are changed (Dropzone or <input type="file">)
  calculateCosts(event) {
    // Handle direct file input change
    if (event.target.files) {
      this.files = Array.from(event.target.files)
      this.costs.clear()
      this.files.forEach(file => {
        if (this.isValidImageFile(file)) {
          this.costs.set(file.name, this.upscalingCreditValue)
        }
      })
    }

    // Handle dropzone event
    if (event.detail?.files) {
      const { files } = event.detail
      const newFileNames = new Set(files.map(({ file }) => file.name))
      
      // Remove files that no longer exist
      this.files = this.files.filter(file => {
        if (!newFileNames.has(file.name)) {
          this.costs.delete(file.name)
          return false
        }
        return true
      })
      
      // Add new files
      files.forEach(({ file }) => {
        if (!this.files.find(f => f.name === file.name) && this.isValidImageFile(file)) {
          this.files.push(file)
          this.costs.set(file.name, this.upscalingCreditValue)
        }
      })
    }

    // If no files, reset
    if (this.files.length === 0) {
      this.resetDisplay()
    } else {
      this.updateDisplay()
    }
  }

  isValidImageFile(file) {
    return file.type.startsWith("image/")
  }

  resetDisplay() {
    const span = this.submitButtonTarget.querySelector("span:last-child")
    if (span) span.textContent = "Upscale" // fallback text
    this.billsContainerTarget.classList.add("hidden")
    this.billsPopupTarget.classList.add("hidden")
    this.arrowTarget.classList.remove("rotate-180")
  }

  updateDisplay() {
    // Sum all costs
    let totalCost = 0
    for (let cost of this.costs.values()) {
      totalCost += cost
    }
    // Round if needed
    const roundedCost = Math.ceil(totalCost * 10) / 10

    // Update the button label: "Upscale (X Credits)"
    const span = this.submitButtonTarget.querySelector('span:last-child')
    if (span) span.textContent = `Upscale (${roundedCost} Credits)`

    // Show the itemized bills
    this.billsContainerTarget.classList.remove("hidden")
    this.itemizedBillsTarget.innerHTML = this.generateItemizedBillsHTML()
  }

  generateItemizedBillsHTML() {
    let html = '<div class="text-white space-y-3">'
    html += '<div class="text-lg font-medium text-gray-200 mb-3">Cost Breakdown</div>'
    html += '<div class="border border-dashed border-gray-600 rounded-lg p-4 space-y-3">'

    // Show each file
    if (this.files.length > 0) {
      // cost is always upscalingCredit per file
      const costPerFile = this.upscalingCreditValue
      html += `<div class="space-y-2">`
      html += `<div class="flex justify-between items-center text-sm">`
      html += `<span>Images (${this.files.length} × ${costPerFile} credits)</span>`
      const totalForImages = this.files.length * costPerFile
      html += `<span>${totalForImages} credits</span>`
      html += `</div></div>`
    }

    html += '</div>' // end of dashed border

    // total cost
    const totalCost = Array.from(this.costs.values()).reduce((sum, c) => sum + c, 0)
    html += `
      <div class="flex justify-between items-center pt-3 text-sm font-medium">
        <span>Total:</span>
        <span class="flex items-center gap-1">
          <svg class="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
          </svg>
          ${Math.ceil(totalCost * 10) / 10}
        </span>
      </div>
    </div>`
    return html
  }

  toggleBills(event) {
    event.stopPropagation()
    this.billsPopupTarget.classList.toggle("hidden")
    this.arrowTarget.classList.toggle("rotate-180")
  }
} 